'use client'

import { Text } from '@components/primitives/Text'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import { TrackingSources } from '@lib/tracking/TrackingSources'
import uniq from '@lib/utilities/uniq'
import type { ConceptsByFrequency } from '@modules/trials/helpers/extractEntitiesFromEligibilityCriteria/types'
import { STRUCTURED_QUESTION_NONE, STRUCTURED_QUESTION_UNSURE } from './const'

const SEARCH_RESULTS_LIMIT = 10

export function PopularItems({
  addNoneOption = false,
  itemsByFrequency,
  onChange,
  title,
  trackingType,
  value,
  valueDisplayCallback = (s) => s,
}: {
  addNoneOption?: boolean
  itemsByFrequency: ConceptsByFrequency
  onChange?: (newValue: string[]) => void
  title: string
  trackingType: string
  value: string[]
  valueDisplayCallback?: (s: string) => string
}) {
  const currentValue = value ?? []
  // TODO: Clean this up and provide more than just 10
  const popularItems = itemsByFrequency
    .sort((a, b) => b.frequency - a.frequency)
    .slice(0, SEARCH_RESULTS_LIMIT)
    .filter((item) => !currentValue.includes(item.name))

  if (popularItems.length === 0) {
    return null
  }

  // id is based on concept id and can vary for each pass through the apply flow
  // we should figure out a unique id to assign to "Unsure" answer - does not need to correspond to a concept id
  const largestPopularItemId =
    Math.max(...popularItems.map((item) => item.id)) ?? -1

  // we don't use frequency for "Unsure" and doesn't have any meaning for unsure
  const popularItemsWithUnsure = [
    ...popularItems,
    // no reason to add "Not Sure" if the user has already selected an answer
    ...(value.length === 0
      ? [
          {
            displayName: STRUCTURED_QUESTION_UNSURE,
            id: largestPopularItemId + 1,
            name: STRUCTURED_QUESTION_UNSURE,
          },
        ]
      : []),
  ]

  // no reason to add "None" if the user has already selected an answer
  if (addNoneOption && value.length === 0) {
    popularItemsWithUnsure.push({
      displayName: STRUCTURED_QUESTION_NONE,
      id: largestPopularItemId + 2,
      name: STRUCTURED_QUESTION_NONE,
    })
  }

  const handlePopularItemSelect = (item: string) => () => {
    trackEvent(TrackingEvents.TRIAL_APPLICATION_SEARCH_POPULAR_ITEM_SELECTED, {
      source: TrackingSources.APPLY_FLOW_V2,
      type: trackingType,
      val: item,
    })

    if (!onChange) {
      return
    }

    if (
      value.includes(STRUCTURED_QUESTION_NONE) ||
      value.includes(STRUCTURED_QUESTION_UNSURE)
    ) {
      onChange([item])
      return
    }

    onChange(uniq([...currentValue, item]))
  }

  return (
    <div className='relative mt-4 flex flex-col gap-2 rounded-lg border-2 border-neutral200 bg-white p-4'>
      <Text
        className='text-neutral900'
        element='p'
        styleName='p-strong'
        value={title}
      />
      <div className='relative flex flex-wrap gap-2'>
        {popularItemsWithUnsure.map((item) => (
          <div
            className='relative flex min-h-10 min-w-20 items-center justify-center gap-4 rounded-full border-2 border-neutral200 bg-neutral50 px-4 hover:cursor-pointer'
            key={item.id}
            onClick={handlePopularItemSelect(item.name)}
          >
            <Text
              className='text-neutral900'
              element='p'
              styleName='p-tiny'
              value={valueDisplayCallback(item.displayName ?? item.name)}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
